// CreateCustomAgentDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { Plus, Bot, FileText, Image as ImageIcon, Video, Mic, PieChart, Users } from 'lucide-react';

const AGENT_TYPE_OPTIONS = [
  { type: 'text', icon: FileText, label: 'Text', desc: 'Uses GPT-4o for text generation and analysis' },
  { type: 'image', icon: ImageIcon, label: 'Image', desc: 'Uses GPT Image 1 / Nano Banana for image generation' },
  { type: 'video', icon: Video, label: 'Video', desc: 'Uses Sora 2 for video generation' },
  { type: 'voice', icon: Mic, label: 'Voice', desc: 'Uses OpenAI TTS/STT for voice processing' },
  { type: 'data', icon: PieChart, label: 'Data', desc: 'Uses GPT-4o for data analysis and visualization' },
  { type: 'leader', icon: Users, label: 'Leader', desc: 'Coordinates other agents, provides direction and manages workflows' },
];

const PRIMARY_MODEL_OPTIONS = [
  { value: 'gpt-4o', label: 'GPT-4o (Text/Analysis)' },
  { value: 'gpt-image-1', label: 'GPT Image 1 (Images)' },
  { value: 'nano-banana', label: 'Gemini Nano Banana (Images)' },
  { value: 'sora-2', label: 'Sora 2 (Video)' },
  { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (Creative Text)' },
  { value: 'gemini-3-pro', label: 'Gemini 3 Pro (Synthesis)' },
];

const CAPABILITY_OPTIONS = [
  'Generate Images', 'Generate Videos', 'Data Analysis', 'Content Writing', 
  'Code Generation', 'Voice Synthesis', 'Translation', 'Summarization', 
  'Leadership/Direction', 'Reporting'
];

/**
 * Dialog for creating a new custom AI agent
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.formData - Current form data
 * @param {function} props.onFormChange - Callback when form data changes
 * @param {function} props.onSubmit - Callback when form is submitted
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const CreateCustomAgentDialog = ({
  open,
  onOpenChange,
  formData,
  onFormChange,
  onSubmit,
  onCancel
}) => {
  const toggleCapability = (cap) => {
    const caps = formData.capabilities?.includes(cap)
      ? formData.capabilities.filter(c => c !== cap)
      : [...(formData.capabilities || []), cap];
    onFormChange({ ...formData, capabilities: caps });
  };

  const currentTypeDesc = AGENT_TYPE_OPTIONS.find(t => t.type === formData.agent_type)?.desc || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create Custom AI Agent
          </DialogTitle>
          <DialogDescription>
            Design a new AI agent with specific capabilities
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Agent Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
                placeholder="e.g., Maya"
                data-testid="create-agent-name"
              />
            </div>
            <div>
              <Label>Role Title *</Label>
              <Input
                value={formData.role_title || ''}
                onChange={(e) => onFormChange({ ...formData, role_title: e.target.value })}
                placeholder="e.g., Creative Designer"
                data-testid="create-agent-role"
              />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
              placeholder="Describe what this agent does..."
              rows={2}
              data-testid="create-agent-description"
            />
          </div>

          <div>
            <Label>Agent Type</Label>
            <div className="grid grid-cols-6 gap-2 mt-2">
              {AGENT_TYPE_OPTIONS.map(({ type, icon: Icon, label }) => (
                <Button
                  key={type}
                  variant={formData.agent_type === type ? 'default' : 'outline'}
                  className={`h-auto py-3 flex flex-col items-center gap-1 ${
                    formData.agent_type === type ? 'bg-violet-500 hover:bg-violet-600' : ''
                  }`}
                  onClick={() => onFormChange({ ...formData, agent_type: type })}
                  data-testid={`create-agent-type-${type}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{label}</span>
                </Button>
              ))}
            </div>
            {currentTypeDesc && (
              <p className="text-xs text-muted-foreground mt-2">{currentTypeDesc}</p>
            )}
          </div>

          <div>
            <Label>Primary AI Model</Label>
            <Select 
              value={formData.primary_model || 'gpt-4o'} 
              onValueChange={(val) => onFormChange({ ...formData, primary_model: val })}
            >
              <SelectTrigger data-testid="create-agent-model">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIMARY_MODEL_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Capabilities</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {CAPABILITY_OPTIONS.map((cap) => (
                <Badge
                  key={cap}
                  variant={formData.capabilities?.includes(cap) ? 'default' : 'outline'}
                  className={`cursor-pointer ${formData.capabilities?.includes(cap) ? 'bg-violet-500' : ''}`}
                  onClick={() => toggleCapability(cap)}
                  data-testid={`create-agent-cap-${cap.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cap}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="create-agent-cancel">
            Cancel
          </Button>
          <Button 
            disabled={!formData.name || !formData.role_title}
            onClick={onSubmit}
            data-testid="create-agent-submit"
          >
            <Bot className="h-4 w-4 mr-2" />
            Create Agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCustomAgentDialog;
